import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PageHeading } from "@/components/ui/page-heading";
import { EmployeeCreateDialog } from "./components";
import { EmployeeTable } from "./components/EmployeeTable/EmployeeTable";
import { EmployeeEditDialog } from "./components/EmployeeEditDialog/EmployeeEditDialog";
import { EmployeeDeleteDialog } from "./components/EmployeeDeleteDialog/EmployeeDeleteDialog";
import {
  SearchFilterBar,
  FilterSettings,
} from "@/components/ui/search-filter-bar";
import { appSettingsService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import type { Employee } from "@/types/employee.types";
import { AppSettings } from "@/types/app-settings.types";
import { Plus } from "lucide-react";

export const EmployeesListView = () => {
  const { hasRole } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    searchTerm: "",
    filters: {},
  });

  // Fetch departments from app settings
  const { data: appSettings } = useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => appSettingsService.getAll(),
  });

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEditOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDeleteOpen(true);
  };

  // Filter options for employees
  const filterOptions = [
    {
      key: "department",
      label: "Department",
      type: "select" as const,
      options: (
        appSettings?.department_list?.map((dept) => ({
          value: dept,
          label: dept,
        })) || []
      ).sort((a, b) => a.label.localeCompare(b.label)),
    },
    // Only show hourly rate filter for managers and superadmins
    ...(hasRole(UserRole.MANAGER)
      ? [
          {
            key: "hourlyRate",
            label: "Hourly Rate",
            type: "range" as const,
            rangeType: "number" as const,
          },
        ]
      : []),
    {
      key: "isActive",
      label: "Status",
      type: "select" as const,
      options: [
        { value: "true", label: "Active" },
        { value: "false", label: "Inactive" },
      ],
    },
  ];

  return (
    <div>
      <PageHeading
        title="Employees"
        subtitle="Manage employees and their details"
        className="mb-6"
        actions={
          hasRole(UserRole.MANAGER) ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Employee
            </Button>
          ) : undefined
        }
      />

      <SearchFilterBar
        filterOptions={filterOptions}
        placeholder="Search employees by name, email, or internal ID..."
        className="mb-6"
        onFilterChange={setFilterSettings}
      />

      <EmployeeCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EmployeeEditDialog
        employee={selectedEmployee}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <EmployeeDeleteDialog
        employee={selectedEmployee}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />

      <EmployeeTable
        filterSettings={filterSettings}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
};
